import { useState, useEffect, useRef } from 'react';
import { useDashboard } from '../../hooks/useDashboard';
import styles from './Header.module.css';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const SHORT  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmt(d) { return d ? `${SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : ''; }

export default function Header() {
  const { stores, selectedStores, toggleStore, selectAllStores, clearAllStores, dateRange, setDateRange, activeStore, exitStore } = useDashboard();

  const [drpOpen, setDrpOpen]       = useState(false);
  const [drpYear, setDrpYear]       = useState(new Date().getFullYear());
  const [drpMonth, setDrpMonth]     = useState(new Date().getMonth());
  const [selecting, setSelecting]   = useState(false);
  const drpRef = useRef(null);

  const [panelOpen, setPanelOpen]   = useState(false);
  const [storeSearch, setStoreSearch] = useState('');
  const storeRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (drpRef.current && !drpRef.current.contains(e.target)) setDrpOpen(false);
      if (storeRef.current && !storeRef.current.contains(e.target)) setPanelOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function drpNav(dir) {
    let m = drpMonth + dir, y = drpYear;
    if (m > 11) { m = 0; y++; } if (m < 0) { m = 11; y--; }
    setDrpMonth(m); setDrpYear(y);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  function drpClick(day) {
    const dt = new Date(drpYear, drpMonth, day);
    if (dt > today) return;
    if (!dateRange.start || (!selecting && dateRange.end)) {
      setDateRange({ start: dt, end: null }); setSelecting(true);
    } else {
      const sorted = dt < dateRange.start ? { start: dt, end: dateRange.start } : { start: dateRange.start, end: dt };
      setDateRange(sorted); setSelecting(false);
      setTimeout(() => setDrpOpen(false), 180);
    }
  }

  const firstDay    = new Date(drpYear, drpMonth, 1).getDay();
  const daysInMonth = new Date(drpYear, drpMonth + 1, 0).getDate();

  const triggerLabel = dateRange.start && dateRange.end
    ? `${fmt(dateRange.start)} – ${fmt(dateRange.end)}`
    : 'Select date range';

  const filteredStores = stores.filter(s => s.name.toLowerCase().includes(storeSearch.toLowerCase()));
  const allSelected    = selectedStores.size === stores.length;
  const triggerStoreTxt = allSelected
    ? `All ${stores.length} Stores`
    : selectedStores.size === 1
    ? `A2B ${[...selectedStores][0]}`
    : `${selectedStores.size} stores selected`;

  const crumbLabel = activeStore ? `A2B ${activeStore}` : 'Dashboard';

  return (
    <header className={styles.header}>
      {/* LEFT: brand + breadcrumb stacked */}
      <div className={styles.left}>
        <div className={styles.logoName}>TangoEye</div>
        <div className={styles.breadcrumb}>
          {activeStore && (
            <>
              <button className={styles.bcBack} onClick={exitStore}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                All Stores
              </button>
              <span className={styles.bcSep}>/</span>
            </>
          )}
          <span className={styles.bcBrand} onClick={activeStore ? exitStore : undefined} style={activeStore ? { cursor: 'pointer' } : {}}>
            TangoEye
          </span>
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ flexShrink: 0 }}>
            <path d="M2 1l4 3.5-4 3.5" stroke="var(--g400)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className={styles.bcCrumb}>{crumbLabel}</span>
        </div>
      </div>

      <div className={styles.spacer} />

      {/* RIGHT: filters */}
      <div className={styles.filters}>
        {/* Date Range */}
        <div className={styles.filterGroup}>
          <div ref={drpRef} className={styles.dropWrap}>
            <button className={`${styles.trigger} ${drpOpen ? styles.open : ''}`} onClick={() => setDrpOpen(v => !v)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span className={styles.triggerTxt}>{triggerLabel}</span>
              <span className={styles.chevron}>▼</span>
            </button>
            {drpOpen && (
              <div className={styles.panel}>
                <div className={styles.calHeader}>
                  <button className={styles.navBtn} onClick={() => drpNav(-1)}>‹</button>
                  <span className={styles.monthLabel}>{MONTHS[drpMonth]} {drpYear}</span>
                  <button className={styles.navBtn} onClick={() => drpNav(1)}>›</button>
                </div>
                <div className={styles.weekdays}>
                  {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className={styles.wd}>{d}</div>)}
                </div>
                <div className={styles.days}>
                  {Array.from({ length: firstDay }, (_, i) => <div key={`e${i}`} />)}
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const dt  = new Date(drpYear, drpMonth, day);
                    const isFuture = dt > today;
                    const isStart = dateRange.start && dt.getTime() === dateRange.start.getTime();
                    const isEnd   = dateRange.end   && dt.getTime() === dateRange.end.getTime();
                    const inRange = dateRange.start && dateRange.end && dt > dateRange.start && dt < dateRange.end;
                    let cls = styles.day;
                    if (isFuture) cls += ` ${styles.dayDisabled}`;
                    if (isStart) cls += ` ${styles.dayStart}`;
                    if (isEnd)   cls += ` ${styles.dayEnd}`;
                    if (inRange) cls += ` ${styles.dayInRange}`;
                    return <div key={day} className={cls} onClick={isFuture ? undefined : () => drpClick(day)}>{day}</div>;
                  })}
                </div>
                <div className={styles.calFooter}>
                  <span className={styles.footerRange}>
                    {!dateRange.start ? 'Select start date' : !dateRange.end ? `${fmt(dateRange.start)} → pick end` : `${fmt(dateRange.start)} – ${fmt(dateRange.end)}`}
                  </span>
                  <button className={styles.clearBtn} onClick={() => { setDateRange({ start: null, end: null }); setSelecting(false); }}>Clear</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stores */}
        <div className={styles.filterGroup}>
          <div ref={storeRef} className={styles.dropWrap}>
            <button className={`${styles.storeTrigger} ${panelOpen ? styles.open : ''}`} onClick={() => { setPanelOpen(v => !v); setStoreSearch(''); }}>
              <span className={styles.triggerTxt}>{triggerStoreTxt}</span>
              <span className={styles.chevron}>▼</span>
            </button>
            {panelOpen && (
              <div className={styles.storePanel}>
                <div className={styles.storeHdr}>
                  <div className={styles.storeSearch}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input autoFocus value={storeSearch} onChange={e => setStoreSearch(e.target.value)} placeholder="Search stores…" className={styles.searchInput} />
                  </div>
                  <div className={styles.selRow}>
                    <button className={styles.selBtn} onClick={selectAllStores}>Select all</button>
                    <span>·</span>
                    <button className={styles.selBtn} onClick={clearAllStores}>Clear all</button>
                  </div>
                </div>
                <div className={styles.storeList}>
                  {filteredStores.length === 0
                    ? <div className={styles.noMatch}>No stores match</div>
                    : filteredStores.map(s => (
                      <div key={s.name} className={`${styles.storeOpt} ${selectedStores.has(s.name) ? styles.checked : ''}`} onClick={() => toggleStore(s.name)}>
                        <div className={styles.chkbox}>
                          {selectedStores.has(s.name) && <svg viewBox="0 0 8 8" fill="none" width="8" height="8"><polyline points="1,4 3,6 7,2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        <span>A2B {s.name}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
